import { baseApi, setBaseQueryImpl } from '@metadevprofile/shared/api';
import { combineReducers, configureStore } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';
import { type TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import {
  createMigrate,
  FLUSH,
  PAUSE,
  PERSIST,
  PURGE,
  persistReducer,
  persistStore,
  REGISTER,
  REHYDRATE,
} from 'redux-persist';
import { impersonationReducer } from '@/admin-user/stores/impersonation-slice';
import { authReducer } from '@/auth/stores/auth-slice';
import { edenBaseQuery } from '@/common/redux/eden-base-query';
import { localStorageAdapter, sessionStorageAdapter } from '@/common/redux/key-value-storage';
import { migrateLegacyStores } from '@/common/redux/migrate-legacy-stores';
import { migrations, PERSIST_VERSION } from '@/common/redux/migrations';
import { cookieConsentReducer } from '@/cookie-consent/stores/cookie-consent-slice';
import { preferencesReducer } from '@/profile/stores/preferences-slice';

setBaseQueryImpl(edenBaseQuery);

// Nested persists (redux-persist's own recipe): `auth` and `impersonation` each get their own
// persistReducer + storage instead of living in the root whitelist below, because neither one
// persists the same way as `preferences`/`cookieConsent` — `auth` only persists `user` (loading
// and mfaToken are transient), and `impersonation` persists to sessionStorage, not localStorage,
// and drops `starting`. Each nested persistReducer writes its own `persist:<key>` storage entry
// and rehydrates independently of the root persistor.
const authPersistConfig = {
  key: 'auth',
  storage: localStorageAdapter,
  whitelist: ['user'],
};

const impersonationPersistConfig = {
  key: 'impersonation',
  // sessionStorage (not localStorage): this snapshot holds the admin's own access + refresh
  // tokens while impersonating. The app otherwise persists tokens in localStorage for normal
  // sessions, but that would leave the admin's tokens sitting around indefinitely on shared or
  // long-lived machines. sessionStorage narrows that window to the current tab's lifetime
  // (cleared on tab close) while still surviving a page reload during impersonation.
  storage: sessionStorageAdapter,
  blacklist: ['starting'],
};

const rootReducer = combineReducers({
  [baseApi.reducerPath]: baseApi.reducer,
  auth: persistReducer(authPersistConfig, authReducer),
  preferences: preferencesReducer,
  cookieConsent: cookieConsentReducer,
  impersonation: persistReducer(impersonationPersistConfig, impersonationReducer),
});

const persistConfig = {
  key: 'metadevprofile-root',
  version: PERSIST_VERSION,
  storage: localStorageAdapter,
  // RTK Query's own cache (health data) must never hit localStorage. `auth` and `impersonation`
  // manage their own persistence above (nested persists) and are deliberately excluded here.
  whitelist: ['preferences', 'cookieConsent'],
  migrate: createMigrate(migrations, { debug: false }),
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }).concat(baseApi.middleware),
});

// Must run before `persistStore()` below — see migrate-legacy-stores.ts for why the ordering
// matters.
migrateLegacyStores(store.dispatch);

setupListeners(store.dispatch);

export const persistor = persistStore(store);

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

export const useAppDispatch: () => AppDispatch = useDispatch;
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
